<template>
    <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">Technical Analysis (1h)</h5>
            <el-tooltip
                class="box-item"
                effect="dark"
                content="Summary based on multiple technical indicators. Refresh page for latest."
                placement="top"
            >
                <el-icon><InfoFilled /></el-icon>
            </el-tooltip>
        </div>
        <div class="card-body">
            <div v-if="loading" class="text-center">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>
            <div v-else-if="error" class="alert alert-danger" role="alert">
                {{ error }}
            </div>
            <div v-else-if="indicators" class="ta-summary">
                <!-- Summary Signal -->
                <div class="summary-signal text-center mb-4">
                    <span :class="['signal-badge', signalClass]">{{ indicators.summary?.signal || 'Neutral' }}</span>
                    <div class="text-muted small mt-1">Confidence: {{ indicators.summary?.confidence || 'Low' }}</div>
                </div>

                <!-- Indicator Details Table -->
                <table class="table table-sm table-borderless ta-table">
                    <tbody>
                        <!-- Oscillators Section - only show if we have oscillator data -->
                        <template v-if="hasOscillatorData">
                            <tr>
                               <td colspan="3" class="text-muted pt-3 fw-bold">Oscillators</td>
                            </tr>
                            <tr v-if="hasValue(indicators.rsi?.[14])">
                                <td>RSI (14)</td>
                                <td class="text-end">{{ formatValue(indicators.rsi?.[14]) }}</td>
                                <td :class="['text-end', rsiSignalClass]">{{ rsiSignalText }}</td>
                            </tr>
                            <tr v-if="hasValue(stochKValue)">
                               <td>Stochastic %K (14, 3, 3)</td>
                               <td class="text-end">{{ formatValue(stochKValue) }}</td>
                               <td :class="['text-end', stochSignalClass]">{{ stochSignalText }}</td>
                           </tr>
                            <tr v-if="hasValue(stochDValue)">
                               <td>Stochastic %D (3)</td>
                               <td class="text-end">{{ formatValue(stochDValue) }}</td>
                               <td></td>
                           </tr>
                           <tr v-if="hasValue(indicators.cci?.[20])">
                                <td>CCI (20)</td>
                                <td class="text-end">{{ formatValue(indicators.cci?.[20]) }}</td>
                                <td :class="['text-end', cciSignalClass]">{{ cciSignalText }}</td>
                            </tr>
                            <tr v-if="hasValue(indicators.willr?.[14])">
                                <td>Williams %R (14)</td>
                                <td class="text-end">{{ formatValue(indicators.willr?.[14]) }}</td>
                                <td :class="['text-end', willrSignalClass]">{{ willrSignalText }}</td>
                            </tr>
                        </template>

                        <!-- Trend/Momentum Section - only show if we have trend data -->
                        <template v-if="hasTrendData">
                            <tr>
                                <td colspan="3" class="text-muted pt-3 fw-bold">Trend & Momentum</td>
                            </tr>
                            <tr v-if="hasValue(indicators.macd?.macd)">
                                <td>MACD Line</td>
                                 <td class="text-end">{{ formatValue(indicators.macd?.macd) }}</td>
                                 <td :class="['text-end', macdSignalClass]">{{ macdSignalText }}</td>
                            </tr>
                             <tr v-if="hasValue(indicators.macd?.signal)">
                                <td>MACD Signal</td>
                                 <td class="text-end">{{ formatValue(indicators.macd?.signal) }}</td>
                                 <td></td>
                            </tr>
                             <tr v-if="hasValue(indicators.macd?.histogram)">
                                <td>MACD Hist</td>
                                 <td class="text-end">{{ formatValue(indicators.macd?.histogram) }}</td>
                                 <td></td>
                            </tr>
                             <tr v-if="hasValue(indicators.adx?.[14])">
                                <td>ADX (14)</td>
                                <td class="text-end">{{ formatValue(indicators.adx?.[14]) }}</td>
                                <td class="text-end text-muted">{{ adxTrendStrength }}</td>
                            </tr>
                             <tr v-if="hasValue(indicators.obv)">
                                <td>On Balance Volume (OBV)</td>
                                <td class="text-end">{{ formatLargeNumber(indicators.obv) }}</td>
                                <td></td>
                            </tr>
                        </template>

                        <!-- Moving Averages Section - only show if we have MA data -->
                        <template v-if="hasMovingAverageData">
                            <tr>
                                <td colspan="3" class="text-muted pt-3 fw-bold">Moving Averages</td>
                            </tr>
                            <tr v-if="hasValue(indicators.ema?.[10])">
                                <td>EMA (10)</td>
                                <td class="text-end">{{ formatValue(indicators.ema?.[10]) }}</td>
                                <td :class="['text-end', ema10SignalClass]">{{ ema10SignalText }}</td>
                            </tr>
                             <tr v-if="hasValue(indicators.sma?.[10])">
                                <td>SMA (10)</td>
                                <td class="text-end">{{ formatValue(indicators.sma?.[10]) }}</td>
                                 <td></td>
                            </tr>
                             <tr v-if="hasValue(indicators.ema?.[20])">
                                <td>EMA (20)</td>
                                <td class="text-end">{{ formatValue(indicators.ema?.[20]) }}</td>
                                <td :class="['text-end', ema20SignalClass]">{{ ema20SignalText }}</td>
                            </tr>
                             <tr v-if="hasValue(indicators.sma?.[20])">
                                <td>SMA (20)</td>
                                <td class="text-end">{{ formatValue(indicators.sma?.[20]) }}</td>
                                 <td></td>
                            </tr>
                             <tr v-if="hasValue(indicators.ema?.[50])">
                                <td>EMA (50)</td>
                                <td class="text-end">{{ formatValue(indicators.ema?.[50]) }}</td>
                                <td :class="['text-end', ema50SignalClass]">{{ ema50SignalText }}</td>
                            </tr>
                             <tr v-if="hasValue(indicators.sma?.[50])">
                                <td>SMA (50)</td>
                                <td class="text-end">{{ formatValue(indicators.sma?.[50]) }}</td>
                                 <td></td>
                            </tr>
                             <tr v-if="hasValue(indicators.ema?.[100])">
                                <td>EMA (100)</td>
                                <td class="text-end">{{ formatValue(indicators.ema?.[100]) }}</td>
                                <td :class="['text-end', ema100SignalClass]">{{ ema100SignalText }}</td>
                            </tr>
                             <tr v-if="hasValue(indicators.sma?.[100])">
                                <td>SMA (100)</td>
                                <td class="text-end">{{ formatValue(indicators.sma?.[100]) }}</td>
                                 <td></td>
                            </tr>
                             <tr v-if="hasValue(indicators.ema?.[200])">
                                <td>EMA (200)</td>
                                <td class="text-end">{{ formatValue(indicators.ema?.[200]) }}</td>
                                <td :class="['text-end', ema200SignalClass]">{{ ema200SignalText }}</td>
                            </tr>
                             <tr v-if="hasValue(indicators.sma?.[200])">
                                <td>SMA (200)</td>
                                <td class="text-end">{{ formatValue(indicators.sma?.[200]) }}</td>
                                 <td></td>
                            </tr>
                        </template>

                         <!-- Volatility Section - only show if we have BB data -->
                         <template v-if="hasBollingerData">
                             <tr>
                                <td colspan="3" class="text-muted pt-3 fw-bold">Volatility</td>
                            </tr>
                             <tr v-if="hasValue(indicators.bbands?.upper)">
                                <td>BB Upper (20, 2)</td>
                                <td class="text-end">{{ formatValue(indicators.bbands?.upper) }}</td>
                                <td></td>
                            </tr>
                             <tr v-if="hasValue(indicators.bbands?.middle)">
                                <td>BB Middle (SMA 20)</td>
                                <td class="text-end">{{ formatValue(indicators.bbands?.middle) }}</td>
                                <td></td>
                            </tr>
                             <tr v-if="hasValue(indicators.bbands?.lower)">
                                <td>BB Lower (20, 2)</td>
                                <td class="text-end">{{ formatValue(indicators.bbands?.lower) }}</td>
                                <td></td>
                            </tr>
                         </template>

                    </tbody>
                </table>
                 <div class="text-center text-muted small mt-2">
                    Last updated: {{ formatTimestamp(indicators.last_updated_timestamp) }}
                    (Price: {{ formatValue(indicators.last_close_price) }})
                </div>
            </div>
             <div v-else class="text-center text-muted">
                No technical analysis data available.
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
// Remove onMounted as fetchData is removed
import { ref, computed } from 'vue';
import { ElTooltip, ElIcon } from 'element-plus'; // Assuming Element Plus is used globally
import { InfoFilled } from '@element-plus/icons-vue'; // Import specific icon

export default {
    name: 'TokenTechnicalAnalysis',
    components: {
        ElTooltip,
        ElIcon,
        InfoFilled, // Register icon component
    },
    props: {
        mint: {
            type: String,
            required: true,
        },
        // Add new prop to receive server-side data
        initialAnalysis: {
            type: Object, // Can be an object or null
            default: null,
        },
    },
    setup(props) {
        // Initialize loading to false since data comes via prop
        const loading = ref(false);
        // Initialize error based on whether initial data was provided
        const error = ref(props.initialAnalysis === null ? 'Technical analysis data could not be loaded.' : null);
        // Initialize indicators directly from the prop
        const indicators = ref(props.initialAnalysis);
        // currentPrice ref can be removed if not used for frontend signals
        // const currentPrice = ref(null);

        // REMOVE the fetchData function and onMounted call:
        /*
        const fetchData = async () => { ... };
        onMounted(fetchData);
        */

        // --- Formatters ---
        // Format values with at least 2 significant figures for small numbers
        const formatValue = (value) => {
            if (value === null || value === undefined) return 'N/A';
            const absValue = Math.abs(value);
            if (absValue === 0) return '0.00';
            if (absValue >= 1000) return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            if (absValue >= 100) return value.toFixed(2);
            if (absValue >= 1) return value.toFixed(4);
            // For values < 1, calculate decimals needed for at least 2 significant figures
            const log = Math.floor(Math.log10(absValue));
            const decimals = Math.max(2, -log + 1);
            return value.toFixed(Math.min(decimals, 12));
        };

        const formatLargeNumber = (value) => {
             if (value === null || value === undefined) return 'N/A';
             // Simple large number formatting (e.g., for OBV)
             return value.toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 1 });
        };

        const formatTimestamp = (timestamp) => {
            if (!timestamp) return 'N/A';
            try {
                // Assuming timestamp is in milliseconds from backend
                return new Date(timestamp).toLocaleString();
            } catch (e) {
                return 'Invalid Date';
            }
        };

        // Keep all computed properties for signal display (signalClass, rsiSignalText, etc.)
        // They will now react to the 'indicators' ref which is populated by the prop.
        const signalClass = computed(() => {
            const signal = indicators.value?.summary?.signal?.toLowerCase() || 'neutral';
            if (signal === 'buy') return 'text-success bg-success-light';
            if (signal === 'sell') return 'text-danger bg-danger-light';
            return 'text-warning bg-warning-light'; // Neutral
        });

        // RSI Signal Logic
        const rsiSignalText = computed(() => {
            const rsi = indicators.value?.rsi?.[14];
            if (rsi === null || rsi === undefined) return '';
            if (rsi < 30) return 'Oversold (Buy)';
            if (rsi > 70) return 'Overbought (Sell)';
            return 'Neutral';
        });
        const rsiSignalClass = computed(() => {
             const rsi = indicators.value?.rsi?.[14];
            if (rsi === null || rsi === undefined) return 'text-muted';
            if (rsi < 30) return 'text-success';
            if (rsi > 70) return 'text-danger';
            return 'text-warning';
        });

        // MACD Signal Logic (based on histogram or crossover implied by backend summary)
         const macdSignalText = computed(() => {
            const macd = indicators.value?.macd?.macd;
            const signal = indicators.value?.macd?.signal;
            if (macd === null || signal === null || macd === undefined || signal === undefined) return '';
            if (macd > signal) return 'Bullish (Buy)';
            if (macd < signal) return 'Bearish (Sell)';
            return 'Neutral';
        });
         const macdSignalClass = computed(() => {
            const macd = indicators.value?.macd?.macd;
            const signal = indicators.value?.macd?.signal;
             if (macd === null || signal === null || macd === undefined || signal === undefined) return 'text-muted';
            if (macd > signal) return 'text-success';
            if (macd < signal) return 'text-danger';
            return 'text-warning';
        });

        // --- EMA Signals (Implement using last_close_price) ---
        const lastPrice = computed(() => indicators.value?.last_close_price);

        const getEmaSignal = (emaValue) => {
            if (lastPrice.value === null || lastPrice.value === undefined || emaValue === null || emaValue === undefined) return { text: '', class: 'text-muted' };
            if (lastPrice.value > emaValue) return { text: 'Above (Buy)', class: 'text-success' };
            if (lastPrice.value < emaValue) return { text: 'Below (Sell)', class: 'text-danger' };
            return { text: 'Neutral', class: 'text-warning' };
        };

        const ema10Signal = computed(() => getEmaSignal(indicators.value?.ema?.[10]));
        const ema10SignalText = computed(() => ema10Signal.value.text);
        const ema10SignalClass = computed(() => ema10Signal.value.class);

        const ema20Signal = computed(() => getEmaSignal(indicators.value?.ema?.[20]));
        const ema20SignalText = computed(() => ema20Signal.value.text);
        const ema20SignalClass = computed(() => ema20Signal.value.class);

        const ema50Signal = computed(() => getEmaSignal(indicators.value?.ema?.[50]));
        const ema50SignalText = computed(() => ema50Signal.value.text);
        const ema50SignalClass = computed(() => ema50Signal.value.class);

        const ema100Signal = computed(() => getEmaSignal(indicators.value?.ema?.[100]));
        const ema100SignalText = computed(() => ema100Signal.value.text);
        const ema100SignalClass = computed(() => ema100Signal.value.class);

        const ema200Signal = computed(() => getEmaSignal(indicators.value?.ema?.[200]));
        const ema200SignalText = computed(() => ema200Signal.value.text);
        const ema200SignalClass = computed(() => ema200Signal.value.class);

        // Stochastic Signal Logic
        const stochKValue = computed(() => indicators.value?.stoch?.k);
        const stochDValue = computed(() => indicators.value?.stoch?.d);

        const stochSignalText = computed(() => {
            const k = stochKValue.value;
            if (k === null || k === undefined) return '';
            if (k < 20) return 'Oversold (Buy)';
            if (k > 80) return 'Overbought (Sell)';
            return 'Neutral';
        });
        const stochSignalClass = computed(() => {
            const k = stochKValue.value;
            if (k === null || k === undefined) return 'text-muted';
            if (k < 20) return 'text-success';
            if (k > 80) return 'text-danger';
            return 'text-warning';
        });

        // CCI (New)
        const cciSignalText = computed(() => {
            const cci = indicators.value?.cci?.[20];
            if (cci === null || cci === undefined) return '';
            if (cci < -100) return 'Oversold (Buy)';
            if (cci > 100) return 'Overbought (Sell)';
            return 'Neutral';
        });
        const cciSignalClass = computed(() => {
            const cci = indicators.value?.cci?.[20];
            if (cci === null || cci === undefined) return 'text-muted';
            if (cci < -100) return 'text-success';
            if (cci > 100) return 'text-danger';
            return 'text-warning';
        });

        // Williams %R (New)
        const willrSignalText = computed(() => {
            const willr = indicators.value?.willr?.[14];
            if (willr === null || willr === undefined) return '';
            if (willr < -80) return 'Oversold (Buy)';
            if (willr > -20) return 'Overbought (Sell)';
            return 'Neutral';
        });
        const willrSignalClass = computed(() => {
            const willr = indicators.value?.willr?.[14];
            if (willr === null || willr === undefined) return 'text-muted';
            if (willr < -80) return 'text-success';
            if (willr > -20) return 'text-danger';
            return 'text-warning';
        });

        // ADX Trend Strength (New)
        const adxTrendStrength = computed(() => {
            const adx = indicators.value?.adx?.[14];
            if (adx === null || adx === undefined) return '';
            if (adx > 50) return 'Very Strong Trend';
            if (adx > 25) return 'Strong Trend';
            if (adx > 20) return 'Trending';
            return 'Weak/No Trend';
        });

        // Helper to check if a value is valid (not null, undefined, or NaN)
        const hasValue = (val) => {
            return val !== null && val !== undefined && !Number.isNaN(val);
        };

        // Section visibility computed properties
        const hasOscillatorData = computed(() => {
            return hasValue(indicators.value?.rsi?.[14]) ||
                   hasValue(indicators.value?.stoch?.k) ||
                   hasValue(indicators.value?.cci?.[20]) ||
                   hasValue(indicators.value?.willr?.[14]);
        });

        const hasTrendData = computed(() => {
            return hasValue(indicators.value?.macd?.macd) ||
                   hasValue(indicators.value?.adx?.[14]) ||
                   hasValue(indicators.value?.obv);
        });

        const hasMovingAverageData = computed(() => {
            return hasValue(indicators.value?.ema?.[10]) ||
                   hasValue(indicators.value?.ema?.[20]) ||
                   hasValue(indicators.value?.ema?.[50]) ||
                   hasValue(indicators.value?.ema?.[100]) ||
                   hasValue(indicators.value?.ema?.[200]) ||
                   hasValue(indicators.value?.sma?.[10]) ||
                   hasValue(indicators.value?.sma?.[20]) ||
                   hasValue(indicators.value?.sma?.[50]) ||
                   hasValue(indicators.value?.sma?.[100]) ||
                   hasValue(indicators.value?.sma?.[200]);
        });

        const hasBollingerData = computed(() => {
            return hasValue(indicators.value?.bbands?.upper) ||
                   hasValue(indicators.value?.bbands?.middle) ||
                   hasValue(indicators.value?.bbands?.lower);
        });

        return {
            loading,
            error,
            indicators,
            formatValue,
            formatLargeNumber,
            formatTimestamp,
            signalClass,
            rsiSignalText,
            rsiSignalClass,
            macdSignalText,
            macdSignalClass,
            ema10SignalText,
            ema10SignalClass,
            ema20SignalText,
            ema20SignalClass,
            ema50SignalText,
            ema50SignalClass,
            ema100SignalText,
            ema100SignalClass,
            ema200SignalText,
            ema200SignalClass,
            stochKValue,
            stochDValue, stochSignalText, stochSignalClass,
            cciSignalText, cciSignalClass,
            willrSignalText, willrSignalClass,
            adxTrendStrength,
            // Data visibility helpers
            hasValue,
            hasOscillatorData,
            hasTrendData,
            hasMovingAverageData,
            hasBollingerData,
        };
    },
};
</script>

<style scoped>
.ta-summary .summary-signal {
    margin-bottom: 1.5rem;
}

.signal-badge {
    display: inline-block;
    padding: 0.5rem 1.5rem;
    border-radius: 20px; /* More rounded */
    font-size: 1.1rem;
    font-weight: 600;
    /* Light background colors */
}

.bg-success-light { background-color: rgba(40, 199, 111, 0.1); }
.bg-danger-light { background-color: rgba(234, 84, 85, 0.1); }
.bg-warning-light { background-color: rgba(255, 159, 67, 0.1); }

.text-success { color: #28c76f !important; }
.text-danger { color: #ea5455 !important; }
.text-warning { color: #ff9f43 !important; }
.text-muted { color: #6c757d !important; }


.ta-table td {
    padding-top: 0.4rem;
    padding-bottom: 0.4rem;
    vertical-align: middle;
}

.ta-table td:nth-child(3) { /* Signal column */
    font-weight: 500;
    font-size: 0.85rem;
}

.card-header .el-icon {
    cursor: help;
    color: #6c757d; /* Muted color */
}

/* Style for section headers */
.ta-table .fw-bold {
    font-size: 0.9rem;
    color: #4e5154 !important; /* Slightly darker than muted */
    border-bottom: 1px solid #eee;
    padding-bottom: 0.3rem;
    margin-bottom: 0.2rem;
}
</style>
